const STATS_KEY = 'bonusly:bizy-sky-high:stats';
const DAILY_KEY_PREFIX = 'bonusly:bizy-sky-high:daily';

export interface BizySwarmRunResult {
	score: number;
	flowersCollected: number;
	launches: number;
	waspsDodged: number;
	dailySeed: string;
	durationSeconds: number;
	endReason: 'nectar' | 'wasp';
	endHazardEmoji?: string;
}

export interface BizySwarmStats {
	bestScore: number;
	bestFlowersCollected: number;
	bestWaspsDodged: number;
	bestDate: string;
	totalRuns: number;
}

export interface BizySwarmSavedRun {
	run: BizySwarmRunResult;
	stats: BizySwarmStats;
	dailyBestScore: number;
	isPersonalBest: boolean;
	isDailyBest: boolean;
}

const DEFAULT_STATS: BizySwarmStats = {
	bestScore: 0,
	bestFlowersCollected: 0,
	bestWaspsDodged: 0,
	bestDate: '',
	totalRuns: 0,
};

export const getBizySwarmDailySeed = (date = new Date()): string => {
	const year = date.getFullYear();
	const month = `${date.getMonth() + 1}`.padStart(2, '0');
	const day = `${date.getDate()}`.padStart(2, '0');
	return `${year}-${month}-${day}`;
};

export const getBizySwarmDailyKey = (dailySeed: string): string => `${DAILY_KEY_PREFIX}:${dailySeed}`;

const safeReadJson = <T>(key: string, fallback: T): T => {
	try {
		const raw = window.localStorage.getItem(key);
		if (!raw) return fallback;
		return JSON.parse(raw) as T;
	} catch {
		return fallback;
	}
};

const safeWriteJson = (key: string, value: unknown): void => {
	try {
		window.localStorage.setItem(key, JSON.stringify(value));
	} catch {
		// Storage may be unavailable in private browsing or test environments.
	}
};

export const loadBizySwarmStats = (): BizySwarmStats => {
	const stats = safeReadJson<Partial<BizySwarmStats>>(STATS_KEY, DEFAULT_STATS);

	return {
		bestScore: typeof stats.bestScore === 'number' && Number.isFinite(stats.bestScore) ? stats.bestScore : 0,
		bestFlowersCollected: typeof stats.bestFlowersCollected === 'number' && Number.isFinite(stats.bestFlowersCollected) ? stats.bestFlowersCollected : 0,
		bestWaspsDodged: typeof stats.bestWaspsDodged === 'number' && Number.isFinite(stats.bestWaspsDodged) ? stats.bestWaspsDodged : 0,
		bestDate: typeof stats.bestDate === 'string' ? stats.bestDate : '',
		totalRuns: typeof stats.totalRuns === 'number' && Number.isFinite(stats.totalRuns) ? stats.totalRuns : 0,
	};
};

export const loadBizySwarmDailyBest = (dailySeed: string): number => {
	const dailyBest = safeReadJson<{ bestScore?: number }>(getBizySwarmDailyKey(dailySeed), { bestScore: 0 });
	return typeof dailyBest.bestScore === 'number' && Number.isFinite(dailyBest.bestScore) ? dailyBest.bestScore : 0;
};

export const saveBizySwarmRun = (run: BizySwarmRunResult): BizySwarmSavedRun => {
	const previousStats = loadBizySwarmStats();
	const previousDailyBest = loadBizySwarmDailyBest(run.dailySeed);
	const isPersonalBest = run.score > previousStats.bestScore;
	const isDailyBest = run.score > previousDailyBest;

	const stats: BizySwarmStats = {
		bestScore: isPersonalBest ? run.score : previousStats.bestScore,
		bestFlowersCollected: Math.max(previousStats.bestFlowersCollected, run.flowersCollected),
		bestWaspsDodged: Math.max(previousStats.bestWaspsDodged, run.waspsDodged),
		bestDate: isPersonalBest ? run.dailySeed : previousStats.bestDate,
		totalRuns: previousStats.totalRuns + 1,
	};
	const dailyBestScore = isDailyBest ? run.score : previousDailyBest;

	safeWriteJson(STATS_KEY, stats);
	safeWriteJson(getBizySwarmDailyKey(run.dailySeed), { bestScore: dailyBestScore });

	return {
		run,
		stats,
		dailyBestScore,
		isPersonalBest,
		isDailyBest,
	};
};

export const formatBizySwarmScore = (score: number): string => new Intl.NumberFormat().format(score);

export const formatBizySwarmHeight = (heightMeters: number): string => `${formatBizySwarmScore(heightMeters)} m`;

export const formatBizySwarmShareText = ({ run, stats, dailyBestScore, isPersonalBest }: BizySwarmSavedRun): string => {
	const headline = isPersonalBest ? 'New personal best' : 'I played Bizy Sky High';

	return [
		`${headline}: ${formatBizySwarmHeight(run.score)}`,
		`Flowers collected: ${run.flowersCollected}`,
		`Launches: ${run.launches}`,
		`Daily Run: ${run.dailySeed}`,
		`My best: ${formatBizySwarmHeight(stats.bestScore)}`,
		`Today's local best: ${formatBizySwarmHeight(dailyBestScore)}`,
		'Can you climb higher?',
	].join('\n');
};
